<template>
  <div class="category">
    <div class="heading" @click="handleDropDownClick()">
      <h4 class="title">{{ filtername }}</h4>
      <span
        :class="configuration[filtername].expanded ? 'up-arrow' : 'down-arrow'">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="inherit"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M16.585 8.58496L12 13.17L7.415 8.58496L6 9.99996L12 16L18 9.99996L16.585 8.58496Z"
            fill="inherit" />
        </svg>
      </span>
    </div>
    <div v-if="configuration[filtername].expanded" class="textinput">
      <span v-if="showClearIcon()" class="label" @click="handleClear()">
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="var(--primary-text)"
          stroke="var(--primary-text)"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M9 9L1 1M1 9L9 1L1 9Z"
            stroke="inherit"
            stroke-width="1"
            stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      </span>
      <input
        v-model="searchText"
        :name="filtername"
        type="input"
        maxlength="20"
        placeholder="Search..."
        autocomplete="off"
        @input="handleInputChanges($event.target.value)" />
    </div>
    <div
      v-if="configuration[filtername].expanded"
      class="content"
      :class="{ scroll: configuration[filtername].hasScroll }">
      <Checkbox
        v-for="(item, index) in configuration[filtername].options"
        :id="index"
        :key="index"
        :value="item.value"
        :label="item.label"
        :checked="filter && filter.indexOf(item.value) > -1"
        :onchange="checkboxHandleChange" />
      <span v-if="!configuration[filtername].options.length" class="no-result">
        No result found.
      </span>
    </div>
  </div>
</template>

<script>
// Components
import Checkbox from "@/components/Checkbox/Checkbox";

// Helpers
import { debounce } from "@/utils/helpers";

export default {
  name: "CheckboxSearchFilter",
  components: {
    Checkbox,
  },
  props: {
    filtername: {
      type: String,
      required: true,
    },
    configuration: {
      type: Object,
      required: true,
    },
    classname: {
      type: String,
      required: true,
    },
    filter: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      searchText: "",
    };
  },
  methods: {
    handleDropDownClick() {
      this.$emit("handleFilterToggle", `${this.filtername}`);
    },
    checkboxHandleChange(event) {
      this.$emit("handleFilterChange", {
        type: "checkbox",
        filterName: this.filtername,
        value: event.target.value,
      });
    },
    handleClear() {
      this.searchText = "";
    },
    showClearIcon() {
      return this.searchText.length > 0;
    },
    handleSearch(event) {
      this.$emit("handleSearchChange", `${this.filtername}:${event}`);
    },
    handleInputChanges: debounce(async function (event) {
      this.handleSearch(event);
    }, 500),
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/imports";

.category {
  border-radius: 0.25rem;
  padding: 0.75rem;
  display: flex;
  background-color: var(--layout-bg);
  flex-direction: column;
  margin-bottom: 0.75rem;

  & .heading {
    cursor: pointer;
    justify-content: space-between;
    display: flex;
    align-items: center;
    flex: 1;
    & .down-arrow {
      color: var(--icon-bg);
      fill: var(--icon-bg);
    }
    & .title {
      font-family: "Maersk Text";
      margin: 0;
      font-size: 0.875rem;
      color: var(--primary-text);
    }
  }

  & .textinput {
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-bottom: 1.25rem;
    position: relative;

    & .label {
      position: absolute;
      top: 1.125rem;
      cursor: pointer;
      right: 0.5rem;
      font-size: 0.75rem;
      font-family: "Maersk Text";
      color: var(--label-text);
      margin-bottom: 0.3125rem;
      text-transform: uppercase;
    }

    & input {
      background-color: var(--search-bar-bg);
      border-radius: 0.25rem;
      border: 0.0625rem solid var(--label-text);
      border-color: var(--tab-border);
      font-family: "MaerskText Regular";
      font-size: 0.875rem;
      color: var(--primary-text);
      resize: none;
      padding: 0.375rem;
      margin: 0.625rem 0;
      &:focus {
        outline-style: solid;
      }
    }
  }

  & .content {
    display: flex;
    flex-direction: column;
    padding-top: 0.3125rem;
    max-height: 15.625rem;

    &.scroll {
      overflow-y: auto;
    }

    & .checkbox-area {
      display: flex;
      align-items: center;
      padding: 0.5rem 0;

      & .checkbox {
        display: block;
        position: relative;
        cursor: pointer;
        font-size: 1.375rem;
        line-height: 1rem;
        height: 1rem;
        width: 1rem;
        clear: both;

        & .checkbox-custom {
          position: absolute;
          top: 0;
          left: 0;
          height: 1rem;
          width: 1rem;
          background-color: transparent;
          border-radius: 0.25rem;
          transition: all 0.3s ease-out;
          border: 0.0625rem solid var(--primary-text);

          &:after {
            position: absolute;
            content: "";
            left: 0.5rem;
            top: 0.5rem;
            height: 0;
            width: 0;
            border: var(--primary-text);
            border-width: 0 0.25rem 0.25rem 0;
            transform: rotate(0deg) scale(0);
            opacity: 1;
          }
        }
        & input[type="checkbox"] {
          position: absolute;
          opacity: 0;
          cursor: pointer;
        }
        & input:checked ~ .checkbox-custom {
          background-color: var(--active-nav);
          border-radius: 0.3125rem;
          transform: rotate(0deg) scale(1);
          opacity: 1;
          border: 0.0625rem solid var(--invert-color);

          &:after {
            transform: rotate(45deg) scale(1);
            opacity: 1;
            left: 0.3125rem;
            top: 0.125rem;
            width: 0.25rem;
            height: 0.5rem;
            border: solid var(--alt-color);
            border-width: 0 0.125rem 0.125rem 0;
            background-color: transparent;
            border-radius: 0;
          }
        }
      }
      & label {
        font-family: "Maersk Text";
        font-size: 0.875rem;
        padding-left: 0.375rem;
        color: var(--primary-text);
      }
    }
  }
}
</style>
